import { useMutation } from '@tanstack/react-query';
import { postData } from '../../utils/api';
import { PaymentGatewayHook } from '../../types/payment';
import { PaymentTransactionType, PAYMENT_METHOD, PAYMENT_TRANSACTION_TYPE } from '../../types/enums';
import { BkashIcon, NagadIcon } from '../../assets/icons/payment-providers';
import { PaymentProvider } from '../../types/payment';
import { useExtendedSnackbar } from '../useExtendedSnackbar';
import isEmpty from 'lodash-es/isEmpty';
import { PaymentAuthResult } from '../../types/payment';
import { usePaymentResponseHandler } from '../usePaymentResponseHandler';

type BaseRequest = {
  amount: number | null;
  bank_type: string | null;
  cust_phone?: string | null;
  service_type?: string | null;
};

type Pay2LocalDepositRequest = BaseRequest & {
  redirect_url: string;
  currency: string;
};

type Pay2LocalWithdrawRequest = BaseRequest;

type Payload = Pay2LocalDepositRequest | Pay2LocalWithdrawRequest;
type Response = { redirect_url?: string; message?: string; authResults: PaymentAuthResult[] };

const paymentProviders: PaymentProvider[] = [
  {
    label: 'Bkash',
    value: PAYMENT_METHOD.BKASH,
    icon: BkashIcon,
  },
  {
    label: 'Nagad',
    value: PAYMENT_METHOD.NAGAD,
    icon: NagadIcon,
  },
];

export const usePay2Local: PaymentGatewayHook<Payload, Response> = (transactionType: PaymentTransactionType) => {
  const { enqueueSnackbar } = useExtendedSnackbar();
  const { handlePaymentSuccess, handlePaymentError } = usePaymentResponseHandler();

  const successMessageKey =
    transactionType === PAYMENT_TRANSACTION_TYPE.DEPOSIT ? 'depositRequestSubmitted' : 'withdrawRequestSubmitted';

  const failureMessageKey =
    transactionType === PAYMENT_TRANSACTION_TYPE.DEPOSIT ? 'depositRequestFailed' : 'withdrawRequestFailed';

  const mutation = useMutation<Response, Error, Payload>({
    mutationFn: (data) => postData(`/pay2local/${transactionType}`, data, 'wallet'),
    onSuccess: (response) => {
      if (!isEmpty(response.authResults)) {
        handlePaymentSuccess(response, {
          successMessage: successMessageKey,
        });
        return;
      }

      if (response.redirect_url) {
        window.location.href = response.redirect_url;
        return;
      }

      if (response.message && response.message !== 'Success') {
        enqueueSnackbar(response.message, { variant: 'error' });
        return;
      }

      handlePaymentSuccess(response, {
        successMessage: successMessageKey,
      });
    },
    onError: (error) => {
      handlePaymentError(error, {
        failureMessage: failureMessageKey,
      });
    },
  });

  return { mutation, paymentProviders };
};
